import { useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { BarChart3, Briefcase, LayoutDashboard, Package, Sparkles, Users } from "lucide-react";
import Overview from "./showcase/Overview";
import Crm from "./showcase/Crm";
import Projects from "./showcase/Projects";
import Supply from "./showcase/Supply";
import Finance from "./showcase/Finance";
import Ai from "./showcase/Ai";

const ease = [0.2, 0.8, 0.2, 1];
const TABS = [
  ["overview", "Business Overview", LayoutDashboard, "Everything that needs you, in one calm screen.", Overview],
  ["crm", "CRM", Users, "Every lead scored, followed up and turned into work.", Crm],
  ["projects", "Projects", Briefcase, "One workspace per job: progress, team, deliveries, margin.", Projects],
  ["supply", "Supply", Package, "Plans in. Materials, suppliers and purchase orders out.", Supply],
  ["finance", "Finance", BarChart3, "Live profitability on every job — before it's finished.", Finance],
  ["ai", "TRAIDE Intelligence", Sparkles, "Ask anything. TRAIDE already knows the answer.", Ai],
];

// Interactive product showcase — six real screens, one window, smooth swaps. The strongest visual section after the hero.
export function TrustBar() {
  return (
    <section className="hp-rule relative z-10" data-testid="trust-bar-section">
      <div className="hp-wrap flex flex-col items-center gap-4 py-7 lg:flex-row lg:justify-between">
        <p className="text-[14px] font-medium text-zinc-300" data-testid="trust-bar-heading">One platform. Your entire trade business.</p>
        <ul className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[13px] text-zinc-500" data-testid="trust-bar-tags">{["CRM", "Quotes", "Projects", "Scheduling", "Finance", "Supply", "AI"].map((t, i) => <li key={t} className="flex items-center gap-6">{i > 0 && <span className="hidden h-1 w-1 rounded-full bg-zinc-700 sm:block" />}<span className={t === "AI" ? "flex items-center gap-1.5 text-amber-400" : ""}>{t === "AI" && <Sparkles size={12} />}{t}</span></li>)}</ul>
      </div>
    </section>
  );
}

export default function Showcase({ light = true }) {
  const [tab, setTab] = useState("overview");
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.25 });
  const cur = TABS.find((t) => t[0] === tab);
  const Panel = cur[4];
  return (
    <section ref={ref} className={`hp-section ${light ? "hp-light" : "hp-rule"}`} data-testid="interactive-product-showcase-section">
      <div className="hp-wrap-wide">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl"><div className="hp-eyebrow">Look what it can do</div><h2 className="hp-h2 mt-4">One platform. Every part of the business.</h2></div>
          <p className="hp-lede max-w-md lg:text-right" data-testid="showcase-caption">{cur[3]}</p>
        </div>
        <div className="mt-8 hp-scrollx" role="tablist" data-testid="showcase-tabs">
          {TABS.map(([k, l, I]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={`hp-tab ${tab === k ? "on" : ""}`} data-testid={`showcase-tab-${k}`}><I size={14} className={k === "ai" ? "text-amber-500" : ""} />{l}</button>)}
        </div>
        <div className="relative mt-6" data-testid="showcase-stage" data-tab={tab}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={tab} initial={{ opacity: 0, y: 12, scale: 0.995 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.995 }} transition={{ duration: 0.32, ease }}>
              <div className="hidden lg:block"><Panel height={560} active={inView} /></div>
              <div className="lg:hidden"><div className="overflow-x-auto pb-2"><div className="min-w-[860px]"><Panel height={520} active={inView} /></div></div><p className="mt-2 text-center text-[11.5px] text-zinc-500">Swipe to explore the screen</p></div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
